import $ from 'jquery';
import moment from 'moment-timezone';
import { prCreator } from './pr-creator.js';
import { tables, statusFormatter } from './tables.js';
import { API } from './api.js';
import { uniqueListener, DTFormatDisplayDate, DTFormatForPicker, parsePublishAtInput, pickerToLocal, localePrefix, pickerToSelectedTimezone } from './helpers.js';
import Handlebars from 'handlebars';

const init = () => {
  // initHeaderSize();
  initNavigation();
  initHandlebarsPartials();
  initTooltips();
  initStepsNumbers();
  initCollapsible();
  initDatePickers();
  initStatusFormatting();
  initMultiModal();
  initAuthForms();
  initSettings();
  initSaveAndShowModal();
  initActivityFeed();
  initVideoPauseOnModalClose();
  handleRegistrationButton();
  tables.init();
  const prCreatorInstance = prCreator.init();
  initInquiryModal(prCreatorInstance);
  // After the creator: it reads the totals the creator has just rendered.
  initCreatorPriceBar();
};

// const initHeaderSize = () => {
//   function changeHeaderSize() {
//     let sidebarWidth = $('.sidebar').outerWidth();
//     let header = $('.page-header');
//     let headerWidth = header.outerWidth();
//     header.css({'width': headerWidth - sidebarWidth + 'px', 'margin-left': sidebarWidth});
//   }

//   $(window).off('resize.changeHeaderSize').on('resize.changeHeaderSize', changeHeaderSize).trigger('resize.changeHeaderSize');
// };

// Off-canvas sidebar drawer, active below the `lg` breakpoint (992px). The open state lives in the
// `menu-open` class on the sidebar, the toggler and <body> (which locks page scrolling).
const NAV_OPEN_CLASS = 'menu-open';

const setNavigationOpen = (open) => {
  const toggler = document.querySelector('.menu-toggler');
  const sidebar = document.querySelector('.main-sidebar');

  if (!(toggler && sidebar)) return;

  // The 70px rail keeps its own look while open, so only the full sidebar's toggler flips state.
  if (!(sidebar.classList.contains('main-sidebar-small'))) {
    toggler.classList.toggle(NAV_OPEN_CLASS, open);
  }
  sidebar.classList.toggle(NAV_OPEN_CLASS, open);
  document.body.classList.toggle(NAV_OPEN_CLASS, open);
  toggler.setAttribute('aria-expanded', open ? 'true' : 'false');
};

const closeNavigation = () => setNavigationOpen(false);

const handleNavigationKeydown = (event) => {
  if (event.key === 'Escape') closeNavigation();
};

const initNavigation = () => {
  const toggler = document.querySelector('.menu-toggler');
  const sidebar = document.querySelector('.main-sidebar');
  const backdrop = document.querySelector('.menu-backdrop');

  if (!(toggler && sidebar)) return;

  // Turbolinks swaps <body>, so the toggler and backdrop are fresh nodes on every load and cannot
  // accumulate listeners. The document-level one is keyed on a stable reference instead.
  toggler.addEventListener('click', () => {
    setNavigationOpen(!sidebar.classList.contains(NAV_OPEN_CLASS));
  });

  if (backdrop) backdrop.addEventListener('click', closeNavigation);

  uniqueListener(document, 'keydown', handleNavigationKeydown);

  // A page cached mid-drawer must not come back open.
  closeNavigation();
};


const initHandlebarsPartials = () => {
  Array.from(document.querySelectorAll('script.tpl-partial[data-name]:not(.registered)')).forEach(partial => {
    partial.classList.add('registered');
    Handlebars.registerPartial(partial.dataset.name, partial.innerHTML);
  });
};


const initTooltips = () => {
  $('[data-toggle="tooltip"]').tooltip();
};

const initStepsNumbers = () => {
  // One counter per wizard panel, numbered in panel DOM order — a single panel can hold several
  // `.section-title` blocks (the plans + options step), which must not inflate the step count.
  const stepTitles = [...document.querySelectorAll('.tab-pane')]
    .map((pane) => pane.querySelector('.section-title'))
    .filter(Boolean);

  // The label is a locale string handed over in pageData (releases/_form) with {current}/{total}
  // placeholders; the English fallback only ever applies if the creator is rendered without it.
  const template = (typeof pageData !== 'undefined' && pageData.stepCounter) || 'Step {current}/{total}';

  stepTitles.forEach((stepTitle, index) => {
    const stepCounter = document.createElement('div');
    stepCounter.classList.add('col-12', 'no-gutter', 'd-block', 'd-lg-none', 'text-medium', 'mb-3', 'step-counter');
    stepCounter.textContent = template
      .replace('{current}', String(index + 1))
      .replace('{total}', String(stepTitles.length));
    stepTitle.prepend(stepCounter);
  });
};

// Sticky price bar for the release creator (releases/form/_aside-summary), shown below the `xl`
// breakpoint where the aside price rail is hidden. It computes nothing: pr-creator.js renders the
// order into `.summary .summary-table`, and a MutationObserver mirrors that table's label/value
// pairs into the bar — no creator code has to know the bar exists.
let priceBarObserver = null;

const readSummaryLines = (source) => Array.from(source.querySelectorAll('tr')).map(row => {
  const cells = Array.from(row.children);
  if (!cells.length) return null;

  const hasValue = cells.length > 1;
  const value = hasValue ? cells[cells.length - 1].textContent.trim() : '';
  const label = (hasValue ? cells.slice(0, -1) : cells)
    .map(cell => cell.textContent.trim()).filter(Boolean).join(' ');

  return label || value ? { label, value } : null;
}).filter(Boolean);

// The bar's headline is the order's net amount — prices are quoted before tax, the label says so —
// read from the row the template marks for it. Without that row the headline stays empty rather
// than borrowing another row's figure: a gross amount under an "excl. tax" label would be worse.
const readSummaryHeadline = (source) => {
  const cell = source.querySelector('tr[data-price-bar-total] > :last-child');
  return cell ? cell.textContent.trim() : '';
};

const initCreatorPriceBar = () => {
  const bar = document.querySelector('.price-bar');
  const source = document.querySelector('.summary .summary-table');
  if (!(bar && source)) return;

  const lines = bar.querySelector('.price-bar-lines');
  const total = bar.querySelector('.price-bar-total');
  const toggle = bar.querySelector('.price-bar-toggle');

  const sync = () => {
    lines.textContent = '';

    readSummaryLines(source).forEach(({ label, value }) => {
      const line = document.createElement('div');
      line.className = 'price-bar-line';
      // textContent, never innerHTML: category and option names come from the database.
      const labelElem = document.createElement('span');
      labelElem.textContent = label;
      const valueElem = document.createElement('span');
      valueElem.textContent = value;
      line.append(labelElem, valueElem);
      lines.append(line);
    });

    total.textContent = readSummaryHeadline(source);
  };

  uniqueListener(toggle, 'click', () => {
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    bar.classList.toggle('price-bar-expanded', open);
    lines.hidden = !open;
  });

  if (priceBarObserver) priceBarObserver.disconnect();
  priceBarObserver = new MutationObserver(sync);
  priceBarObserver.observe(source, { childList: true, subtree: true });
  sync();
};

// Turbolinks caches the DOM as it stands; an expanded bar would come back expanded with a stale
// breakdown behind it.
const collapseCreatorPriceBar = () => {
  const bar = document.querySelector('.price-bar');
  if (!bar) return;

  const toggle = bar.querySelector('.price-bar-toggle');
  const lines = bar.querySelector('.price-bar-lines');

  bar.classList.remove('price-bar-expanded');
  if (toggle) toggle.setAttribute('aria-expanded', 'false');
  if (lines) lines.hidden = true;
};

// Account settings — the Security tab's password disclosure (plan decision E). The row's button
// opens the form under it; the form is server-rendered and already open when the last submit was
// rejected (`@password_form_open`), so this only has to handle the click. No modal: a modal that
// must reopen itself carrying server errors needs the same server flag plus JS.
const initSettings = () => {
  const toggle = document.querySelector('[data-security-row-toggle]');
  const disclosure = document.getElementById('password-form');

  if (!toggle || !disclosure) return;

  uniqueListener(toggle, 'click', () => {
    const open = disclosure.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!open) return;

    const firstField = disclosure.querySelector('input:not([type="hidden"])');
    if (firstField) firstField.focus();
  });
};

const initCollapsible = () => {
  $('.btn-card').click(function (e) {
    e.preventDefault();
    $(this).toggleClass('rotated');
    $(this).closest('.card').toggleClass('collapsed');
  });
};

export const updateDatePickerInfo = (input) => {
  const datetime = parsePublishAtInput(input.value);
  // The local-time line used to be found by walking two parents up from `#datetime`. That input is
  // hidden now and no longer shares a wrapper with the line, so the class is the contract.
  const infoMsg = document.querySelector('.publish-local-time');

  if (!infoMsg) {
    return;
  }

  infoMsg.classList.toggle('d-none', !datetime);
  infoMsg.querySelector('span').innerHTML = datetime ? pickerToLocal(datetime) : '';

  const existingPs = infoMsg.querySelectorAll('p.regional-time');
  existingPs.forEach(p => p.remove());

  if (datetime && typeof pageData !== 'undefined' && pageData.order && Array.isArray(pageData.order.selectedRegionsWithPublishingTime)) {
    const uniqueRegions = {}; // to store unique regions
    pageData.order.selectedRegionsWithPublishingTime.forEach(region => {
      if (region.region_time_zone && !uniqueRegions[region.region_name]) {
        const regionalTime = pickerToSelectedTimezone(datetime, region.region_time_zone);

        uniqueRegions[region.region_name] = regionalTime;  // store the region in an object to avoid duplication

        // create an element only if the region has not yet been added
        const regionP = document.createElement('p');
        regionP.classList.add('regional-time', 'mb-0');
        regionP.innerHTML = `<b>${region.region_name}</b>: ${regionalTime}`;
        infoMsg.appendChild(regionP);
      }
    });
  }
};

// Publishing widget — releases/form/_publishing.
//
// `#datetime[name=publish_at]` stays the canonical field and the only one that is posted, but it is
// hidden: what the reader gets is a date segment (its own date-only xdsoft picker), a 30-minute
// time select and a strip of day tiles. All three write the canonical `YYYY.MM.DD HH:mm` string and
// dispatch `change` on it — that event is what the creator's unsaved-changes guard, its error
// clearing and the local/regional time lines all hang off, so nothing may write the value silently.

const PUBLISH_TIME_STEP_MINUTES = 30;
const PUBLISH_DEFAULT_TIME = '09:00';
const PUBLISH_DAY_TILES = 10;
const PUBLISH_DAY_LOOKBACK = 3;
const PUBLISH_DATE_FORMAT = 'YYYY.MM.DD';
const PUBLISH_TIME_FORMAT = 'HH:mm';
const PUBLISH_PICKER_FORMAT = 'Y.m.d'; // xdsoft/PHP syntax, matching PUBLISH_DATE_FORMAT

// releases/confirm.html.erb — the reschedule form. One visible `#datetime` input, no scheduler
// widget and no `pageData`, so the creator's segmented widget below cannot serve it: it gets a
// plain xdsoft picker writing the same canonical `YYYY.MM.DD HH:mm` string the server parses.
const initConfirmDatePicker = (canonical) => {
  const lang = document.body.lang || 'ja';

  $.datetimepicker.setLocale(lang);

  $(canonical).datetimepicker({
    format: DTFormatForPicker,
    minDate: new Date(),
    scrollInput: false,
  });
};

const initDatePickers = () => {
  const scheduler = document.querySelector('.publish-scheduler');
  const canonical = document.querySelector('#datetime');

  // `pageData` is an inline `var` on the creator page only, so it has to be probed, not referenced:
  // releases/confirm.html.erb carries its own `#datetime` on a page that declares no `pageData`.
  if (canonical && !scheduler) {
    initConfirmDatePicker(canonical);
    return;
  }

  if (!scheduler || !canonical || typeof pageData === 'undefined' || !pageData.order) {
    return;
  }

  const dateInput = scheduler.querySelector('.publish-date-input');
  const dateTrigger = scheduler.querySelector('.publish-date-trigger');
  const timeSelect = scheduler.querySelector('.publish-time-select');
  const dayStrip = scheduler.querySelector('.publish-days');
  const earliestSlot = scheduler.querySelector('.publish-earliest-date');
  const holidayNote = scheduler.querySelector('.publish-holiday-note');
  const lang = document.body.lang || 'ja';
  const timezone = (pageData.user && pageData.user.timezone) || 'Japan';

  const minDateTime = moment(moment.tz(pageData.order.minPublishAt, timezone).format('YYYY-MM-DDTHH:mm'));
  minDateTime.add(1.5, 'hour').startOf('hour');
  /*
  Adding 1.5 hours and rounding down gives the user from 0:30 to 1:30 to finish filling out the form, while keeping round hours.

  Details:
    - the 0.5h is added because if backend has minPublishAt set to 3:59 (exactly 48h from now), and we were to use this value,
      then by the time user would finish filling the form, the 3:59 time would be in less then 48h from now,
      and thus would be invalid – so we add half an hour to give the user time to finish filling out the form
    - on top of that, we want to have nice hours, no pesky minutes or anything, so we add an hour and then round down
      (in other words, we set it to the start of the next hour) that way (ignoring the 0.5h explained above)
      3:59 + 1h = 4:59, but rounded down becomes 4:00, and 4:00 + 1h = 5:00, rounded down is still 5:00
    - as a result, this operation gives us (minPublishAt => plus 1.5h => rounded down):
        - 3:00 => 4:30 => 4:00 (1:00 after minPublishAt)
        - 3:29 => 4:59 => 4:00 (0:31 after minPublishAt)
        - 3:30 => 5:00 => 5:00 (1:30 after minPublishAt)
        - 3:59 => 5:29 => 5:00 (1:01 after minPublishAt)
  */

  const minDay = minDateTime.clone().startOf('day');
  const minDayValue = minDay.format(PUBLISH_DATE_FORMAT);

  let selectedDate = null; // PUBLISH_DATE_FORMAT, or null while nothing is scheduled
  let selectedTime = PUBLISH_DEFAULT_TIME;
  let writingCanonical = false; // our own writes must not re-enter through the `change` listener
  let primingPicker = false;

  const asDate = value => moment(value, PUBLISH_DATE_FORMAT);

  // Japanese public holidays the server put on the page, already keyed by PUBLISH_DATE_FORMAT and
  // already named in the page's language (helpers/publishing_holidays_helper.rb). Purely
  // informational: a holiday is as selectable as any other day — the 48-working-hour floor has
  // already stepped over the ones it had to. Known limitation: the map covers today → +3 months,
  // and the xdsoft picker sets no `maxDate`, so days paged past that window carry no mark.
  const holidays = pageData.order.holidays || {};
  const holidayOn = value => (value && holidays[value]) || null;

  // The earliest slot the day can offer: whole days are the picker's unit, so the min *day* is
  // selectable while the hours before minDateTime on it are not.
  const earliestTimeOn = date => date === minDayValue ? minDateTime.format(PUBLISH_TIME_FORMAT) : '00:00';

  const buildTimeOptions = () => {
    const cursor = moment().startOf('day');
    const end = cursor.clone().add(1, 'day');

    while (cursor.isBefore(end)) {
      const value = cursor.format(PUBLISH_TIME_FORMAT);
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      timeSelect.appendChild(option);
      cursor.add(PUBLISH_TIME_STEP_MINUTES, 'minutes');
    }
  };

  // A stored draft (or a test) can hold a time off the 30-minute grid. It gets its own option
  // rather than being rounded — the widget must never rewrite a value the reader did not touch.
  const ensureTimeOption = value => {
    const options = Array.from(timeSelect.options);

    if (options.some(option => option.value === value)) {
      return;
    }

    const option = document.createElement('option');
    option.value = value;
    option.textContent = value;
    timeSelect.insertBefore(option, options.find(existing => existing.value > value) || null);
  };

  const refreshTimeOptions = () => {
    const floor = earliestTimeOn(selectedDate);

    Array.from(timeSelect.options).forEach(option => {
      option.disabled = option.value < floor;
    });
  };

  const snapTimeToEarliest = () => {
    const floor = earliestTimeOn(selectedDate);

    if (selectedTime >= floor) {
      return;
    }

    const firstAllowed = Array.from(timeSelect.options).find(option => option.value >= floor);
    selectedTime = firstAllowed ? firstAllowed.value : selectedTime;
  };

  const renderDays = () => {
    const start = selectedDate
      ? moment.max(asDate(selectedDate).subtract(PUBLISH_DAY_LOOKBACK, 'days'), minDay.clone())
      : minDay.clone();

    dayStrip.innerHTML = '';

    for (let index = 0; index < PUBLISH_DAY_TILES; index += 1) {
      const day = start.clone().add(index, 'days');
      const value = day.format(PUBLISH_DATE_FORMAT);
      const tile = document.createElement('button');

      tile.type = 'button';
      tile.className = 'publish-day';
      tile.dataset.date = value;
      tile.innerHTML = '<span class="publish-day-weekday"></span><span class="publish-day-date"></span>';
      tile.querySelector('.publish-day-weekday').textContent = day.clone().locale(lang).format('ddd');
      tile.querySelector('.publish-day-date').textContent = day.clone().locale(lang).format(lang === 'ja' ? 'M/D' : 'MMM D');

      if (day.isBefore(minDay, 'day')) {
        tile.disabled = true;
      } else {
        tile.addEventListener('click', () => selectDate(value));
      }

      const holiday = holidayOn(value);

      if (holiday) {
        tile.classList.add('publish-day-holiday');
        tile.title = holiday;
      }

      tile.classList.toggle('publish-day-selected', value === selectedDate);
      tile.setAttribute('aria-pressed', String(value === selectedDate));
      dayStrip.appendChild(tile);
    }
  };

  // The strip marks every holiday it shows; this names the one the reader actually picked.
  const renderHolidayNote = () => {
    if (!holidayNote) {
      return;
    }

    const name = holidayOn(selectedDate);

    holidayNote.classList.toggle('d-none', !name);

    if (!name) {
      return;
    }

    holidayNote.querySelector('.publish-holiday-note-text').textContent = holidayNote.dataset.template
      .replace('{date}', asDate(selectedDate).locale(lang).format(DTFormatDisplayDate))
      .replace('{name}', name);
  };

  const render = () => {
    ensureTimeOption(selectedTime);
    refreshTimeOptions();
    timeSelect.value = selectedTime;
    dateInput.value = selectedDate ? asDate(selectedDate).locale(lang).format(DTFormatDisplayDate) : '';
    renderDays();
    renderHolidayNote();
  };

  const writeCanonical = () => {
    const value = selectedDate ? `${selectedDate} ${selectedTime}` : '';

    if (canonical.value === value) {
      return;
    }

    canonical.value = value;
    writingCanonical = true;
    canonical.dispatchEvent(new Event('change'));
    writingCanonical = false;
    updateDatePickerInfo(canonical);
  };

  const selectDate = value => {
    selectedDate = value;
    snapTimeToEarliest();
    render();
    writeCanonical();
  };

  const syncFromCanonical = () => {
    const parsed = parsePublishAtInput(canonical.value);

    selectedDate = parsed ? parsed.format(PUBLISH_DATE_FORMAT) : null;
    selectedTime = parsed ? parsed.format(PUBLISH_TIME_FORMAT) : PUBLISH_DEFAULT_TIME;
    render();
  };

  // xdsoft reads the input it is attached to when it opens, and that input shows a human date, not
  // the picker's format. The current selection is handed over explicitly instead, before every
  // open; the raw value it writes back into the input is replaced by `render()` in the same tick.
  const primePicker = () => {
    primingPicker = true;
    $(dateInput).datetimepicker('setOptions', {
      value: (selectedDate ? asDate(selectedDate) : minDateTime).toDate(),
    });
    primingPicker = false;
    render();
  };

  buildTimeOptions();
  syncFromCanonical();

  if (earliestSlot) {
    earliestSlot.textContent = `${minDateTime.clone().locale(lang).format(DTFormatDisplayDate)} ${minDateTime.format(PUBLISH_TIME_FORMAT)}`;
  }

  $.datetimepicker.setLocale(lang);

  $(dateInput).datetimepicker({
    format: PUBLISH_PICKER_FORMAT,
    formatDate: PUBLISH_PICKER_FORMAT,
    timepicker: false,
    minDate: minDay.toDate(),
    defaultDate: (selectedDate ? asDate(selectedDate) : minDateTime).toDate(),
    scrollInput: false,
    onChangeDateTime: datetime => {
      if (primingPicker || !datetime) {
        return;
      }
      selectDate(moment(datetime).format(PUBLISH_DATE_FORMAT));
    },
    onGenerate: (datetime, $input) => {
      $input.data('xdsoft_datetimepicker')
        .find('td.xdsoft_disabled')
        .attr('title', $input.data('tooltip-disabled-dates'));
    },
  });

  uniqueListener(dateInput, 'mousedown', primePicker);

  if (dateTrigger) {
    uniqueListener(dateTrigger, 'click', () => {
      primePicker();
      $(dateInput).datetimepicker('show');
    });
  }

  uniqueListener(timeSelect, 'change', () => {
    selectedTime = timeSelect.value;
    snapTimeToEarliest();
    render();
    writeCanonical();
  });

  // Anything that sets the canonical value from outside the widget — a server-rendered draft
  // reloaded into a cached page, an E2E helper — announces itself with `change`, and the segments
  // follow it.
  uniqueListener(canonical, 'change', () => {
    if (!writingCanonical) {
      syncFromCanonical();
    }
  });

  updateDatePickerInfo(canonical);
};

const initStatusFormatting = () => {
  Array.from(document.querySelectorAll('[data-format-status][data-status]')).forEach(elem => {
    elem.innerHTML = statusFormatter(elem.dataset.status, { status_name: elem.dataset.statusName });
  })
};

const handleMultiModalChange = (targetContent) => {
  const targets = document.querySelectorAll(`[data-modal-content="${targetContent}"]`);
  if (!targets.length) return;

  Array.from(targets).forEach(target => {
    Array.from(target.parentElement.children).forEach(child => {
      if (typeof child.dataset.modalContent !== 'undefined') {
        child.classList.toggle('d-none', child !== target);
      }
    });
  });
};

const initMultiModal = () => {
  Array.from(document.querySelectorAll('[data-target-content]')).forEach(link => {

    link.addEventListener('click', event => {
      event.preventDefault();
      handleMultiModalChange(link.dataset.targetContent);
    });
  });
};

const handleSaveAndShowModal = e => {
  e.preventDefault();
  $(e.currentTarget.dataset.target).modal('show');
};

const initSaveAndShowModal = () => {
  Array.from(document.querySelectorAll('[data-action="save-and-show-modal"]')).forEach(link => {
    uniqueListener(link, 'click', handleSaveAndShowModal);
  });
};

const handleAuthForm = ({ form, options, data, callback }) => {
  form.addEventListener('submit', e => {
    e.preventDefault();

    Array.from(form.querySelectorAll('.error-msg')).forEach(errMsg => {
      errMsg.classList.add('d-none');
      errMsg.innerHTML = '';
    });

    API.call({ ...options, data: data() }).then(callback).catch(err => {
      // console.log('err.response:', err.response)
      if (err.response.data.error) {
        const errMsg = form.querySelector('.form-error-msg');
        errMsg.classList.remove('d-none');
        errMsg.innerHTML = err.response.data.error;
      }
      if (err.response.data.errors) {
        Object.entries(err.response.data.errors).forEach(([name, msgs]) => {
          const field = form.querySelector(`[name="${name}"]`);
          field.classList.add('error');
          let errMsg = field.nextElementSibling;
          while (errMsg && !errMsg.classList.contains('error-msg')) { errMsg = field.nextElementSibling }
          if (errMsg) {
            errMsg.innerHTML = Array.isArray(msgs) ? msgs.join(' ') : msgs;
            errMsg.classList.remove('d-none');
          }
        });
      }
    });
  });

  Array.from(form.querySelectorAll('[name]')).forEach(field => {
    field.addEventListener('change', e => {
      e.currentTarget.classList.remove('error');
      let errMsg = field.nextElementSibling;
      while (errMsg && !errMsg.classList.contains('error-msg')) { errMsg = errMsg.nextElementSibling }
      if (errMsg) {
        errMsg.classList.add('d-none');
        errMsg.innerHTML = '';
      }
    });
  });
};

const initAuthForms = () => {
  $('#authModal, #authModal-saved').on('hidden.bs.modal', e => {
    window.authCallback = {};
  });

  Array.from(document.querySelectorAll('.auth-form-login')).forEach(form => {
    handleAuthForm({
      form,
      options: {
        endpoint: API.endpoints.users.login,
      },
      data: () => ({
        user: {
          email: form.querySelector('[name="email"]').value,
          password: form.querySelector('[name="password"]').value,
          remember_me: form.querySelector('[name="remember_me"]').checked ? 1 : 0,
        },
      }),
      // callback: res => {
      // },
    });
  });

  Array.from(document.querySelectorAll('.auth-form-signup')).forEach(form => {
    handleAuthForm({
      form,
      options: {
        endpoint: API.endpoints.users.signup,
      },
      data: () => ({
        user: {
          email: form.querySelector('[name="email"]').value,
          ...(form.querySelector('[name="password"') && {
            password: form.querySelector('[name="password"]').value,
            password_confirmation: form.querySelector('[name="password_confirmation"]').value,
          }),
        },
      }),
      callback: res => {
        const tag = { category: 'user action', action: 'signup completed', label: 'popup in PR creation' };
        try {
          gtag('event', 'frontend', tag);
        } catch (e) {
          console.warn('Cannot set gtag event:', tag);
        }

        if (window.authCallback && typeof window.authCallback.signup === 'function') {
          $('.modal.show').modal('hide');
          window.authCallback.signup();
        } else {
          handleMultiModalChange('thank-you-signup');
        }
      },
    });
  });

  Array.from(document.querySelectorAll('.auth-form-resetpwd')).forEach(form => {
    handleAuthForm({
      form,
      options: {
        endpoint: API.endpoints.users.resetPassword,
      },
      data: () => ({
        user: {
          email: form.querySelector('[name="email"]').value,
        },
      }),
      callback: res => handleMultiModalChange('thank-you-resetpwd'),
    });
  });
};

const initActivityFeed = () => {
  const forms = document.querySelectorAll('.activity-form-comment:not(.started)');
  for (let form of forms) {
    form.addEventListener('submit', e => {
      e.preventDefault();
      try {
        const message = e.currentTarget.querySelector('[name="message"]').value;
        if (message) {
          API.call({
            endpoint: API.endpoints.release.notifications,
            params: { releaseUrl: window.location.href.replace(/(\/releases\/[^/?]+)([/?].*)*$/, '$1') },
            data: {
              release_notification: {
                message,
                notification_type: 'custom_message'
              }
            },
          }).then(res => {
            window.location.reload();
          });
        }
      } catch (err) {
        console.warn('error sending comment', err);
      }
    });
  }
};

const initVideoPauseOnModalClose = () => {
  $('.modal').on('hide.bs.modal', (e) => {
    $('iframe[src*="enablejsapi"]', e.currentTarget).each((i, iframe) => {
      iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    })
  });
};

const initInquiryModal = (prCreatorInstance) => {
  if (prCreatorInstance) {
    return;
  }

  const modals = {
    inquiry: document.querySelector('#modal-inquiry'),
    inquiryConfirmation: document.querySelector('#modal-inquiry-confirmation'),
  };

  if (!modals.inquiry || !modals.inquiryConfirmation) {
    return;
  }

  const inquiry = {
    triggers: document.querySelectorAll('.send-inquiry-btn'),
    msg: modals.inquiry.querySelector('[name="inquiry-msg"]'),
    submit: modals.inquiry.querySelector('#submit-inquiry'),
  };

  const orderId = window.location.href.replace(/^.*\/releases\/([^/?]+)([/?].*)*$/, '$1');

  const openInquiryModal = (e) => {
    e.preventDefault();
    $(modals.inquiry).modal('show');
  };

  const submitInquiry = (e) => {
    e.preventDefault();

    try {
      const message = inquiry.msg.value;
      if (message) {
        API.call({
          endpoint: API.endpoints.release.notifications,
          params: { releaseUrl: window.location.href.replace(/(\/releases\/[^/?]+)([/?].*)*$/, '$1') },
          data: {
            release_notification: {
              message,
              notification_type: 'custom_message'
            }
          },
        }).then(res => {
          Array.from(modals.inquiryConfirmation.querySelectorAll('[data-href-pattern]')).forEach(link => {
            link.href = link.dataset.hrefPattern.replace(/\{id\}/g, orderId);
          })

          // Both dialogs fade, and Bootstrap 4 tears the first one down (backdrop,
          // `body.modal-open`) only on `hidden.bs.modal` — showing the second inside that window
          // lets the teardown strip the second one's backdrop and scroll lock. Hand over after.
          if (modals.inquiry.classList.contains('show')) {
            $(modals.inquiry).one('hidden.bs.modal', () => $(modals.inquiryConfirmation).modal('show'));
            $(modals.inquiry).modal('hide');
          } else {
            $(modals.inquiryConfirmation).modal('show');
          }
        });
      }
    } catch (err) {
      console.warn('error sending comment', err);
    }
  };

  Array.from(inquiry.triggers).forEach(trigger => {
    uniqueListener(trigger, 'click', openInquiryModal);
  })
  uniqueListener(inquiry.submit, 'click', submitInquiry);
};

const handleRegistrationButton = () => {
  const checkbox = document.getElementById('user_privacy_policy_consent');
  if (!checkbox) return;  // Exit if checkbox isn't on the current page

  const submitButton = document.querySelector('.btn-primary');
  checkbox.addEventListener('change', function () {
    submitButton.disabled = !this.checked;
  });
}

document.addEventListener("turbolinks:load", () => init());

// Module scope: evaluated once per full page load, so this listener is never registered twice.
// Turbolinks snapshots the DOM before leaving a page — clear the drawer's open state first, or the
// cached page is restored with the drawer showing and <body> scroll-locked.
// Bootstrap keeps a dropdown's open state in the DOM (`.show` on the menu and its parent,
// aria-expanded on the toggle), so a page snapshotted with the account menu down comes back with it
// down and no listener to close it.
const closeOpenDropdowns = () => {
  document.querySelectorAll('.dropdown-menu.show').forEach((menu) => {
    menu.classList.remove('show');
    if (menu.parentElement) menu.parentElement.classList.remove('show');
  });

  document.querySelectorAll('[data-toggle="dropdown"][aria-expanded="true"]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', 'false');
  });
};

// The editor bar's draft state is persistent, but "Draft saved" / "Unsaved changes" report what
// happened in one visit; a cached page that came back wearing them would be reporting a session the
// reader has left. The baseline the server rendered (`data-initial`) is what the page was born
// with, so that is what the cached copy is put back to.
const resetCreatorDraftStatus = () => {
  const status = document.querySelector('[data-creator-status]');
  if (!status) return;

  status.textContent = status.dataset.initial || '';
  status.classList.remove('editor-bar-status-unsaved');
  // The baseline of a release that was never saved is the placeholder, which is a state of its own:
  // its class is what keeps the "draft is stored" glyph off the line.
  status.classList.toggle('editor-bar-status-placeholder', status.dataset.initialPlaceholder === 'true');
};

// Turbolinks caches the DOM, so an open password disclosure — and whatever was typed into it —
// would come back with the cached page. Both go (system.md, "Responsive").
const closePasswordDisclosure = () => {
  const disclosure = document.getElementById('password-form');

  if (!disclosure) return;

  disclosure.classList.remove('is-open');

  const toggle = document.querySelector('[data-security-row-toggle]');
  if (toggle) toggle.setAttribute('aria-expanded', 'false');

  const form = disclosure.querySelector('form');
  if (form) form.reset();
};

document.addEventListener("turbolinks:before-cache", () => {
  closeNavigation();
  collapseCreatorPriceBar();
  closeOpenDropdowns();
  resetCreatorDraftStatus();
  closePasswordDisclosure();
});
